<template>
  <a
    :href="listingUrl"
    class="relative block peer group"
    :title="`${listing.title} by ${listing.artist.full_name} in ${listing.dimensions_short}, ${listing.medium.title}`"
  >
    <div class="sold-ribbon-container text-uppercase text-white" v-if="soldRibbon && listing.sold">
      <div class="sold-ribbon">Sold</div>
    </div>

    <div
      class="bg-gray-100 overflow-hidden"
      :style="`aspect-ratio: auto ${listing.images.primary_attachment_aspect_ratio};`"
    >
      <div
        v-if="listing.reprint && !listing.sold"
        class="absolute z-[2] top-5 left-5 py-2 px-4 flex items-center space-x-2 text-bt-blue-500 bg-white rounded-full shadow"
      >
        <i class="far fa-image"></i>
        <span class="font-semibold">Print</span>
      </div>
      <img
        class="mx-auto w-full h-full object-cover lazyload transition-transform ease-in-out duration-700 group-hover:scale-110"
        :id="'ListingImg__' + listing.id"
        :data-src="
          $mq
            | mq({
              xs: listing.images.artworks.mobile,
              sm: listing.images.artworks.tablet,
              md: listing.images.artworks.desktop,
            })
        "
        :src="listing.images.artworks.tiny"
        :alt="`${listing.title} by ${listing.artist.full_name} in ${listing.dimensions_short}, ${listing.medium.title}`"
      />
    </div>
    <div class="px-4 py-6">
      <div class="flex items-center">
        <strong class="font-normal text-sm text-uppercase mr-3 text-zinc-900 capitalize line-clamp-1">
          {{ listing.title | truncate(29, "...") }}
        </strong>

        <div class="ml-auto flex items-center">
          <Favourite :listing="listing" />
        </div>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-zinc-400 text-sm capitalize line-clamp-1">
          {{ listing.artist.full_name }}
        </span>
      </div>

      <div class="flex flex-row flex-wrap items-start text-zinc-400 text-xs mt-4">
        <template v-if="listing.reprint">
          <p>
            Print on Canvas,
            <template v-if="listing.reprint"> Multiple Sizes </template>
            <template v-else>
              {{ listing.dimensions_short }}
            </template>
          </p>
        </template>
        <template v-else>
          <p>
            <Medium :listing="listing" class="inline" />
            <span v-if="listing.available_for_framing">, Framed</span>
            <span v-if="listing.frame">, Framed by Artist</span>
          </p>
          <p>, {{ listing.dimensions_short }}</p>
        </template>
      </div>

      <div class="justify-between flex items-center mt-5">
        <span class="font-bold text-zinc-900 text-sm">
          <div>{{ priceLabel }}</div>
          <div class="text-xs mt-2" v-if="listing.price_with_discount">
            <span class="font-normal line-through text-red-600 mr-4">{{ listing.price.format }}</span>
            <span class="bg-red-600 text-white p-2 rounded-md">{{ listing.discount_percentage | discountLabel }}</span>
          </div>
        </span>
      </div>

      <div v-if="isAdmin" class="justify-between flex flex-col md:flex-row mt-1">
        <div>verified: {{ listing.verified }}</div>

        <div v-if="shortlistVisible" class="text-xs">
          <a href="#" @click.prevent="showShortlistModal = true">+ Shortlist</a>
        </div>

        <ShortlistModal
          v-if="showShortlistModal"
          @close="showShortlistModal = false"
          :listingId="listing.id"
          :apiUrl="listing.competition_results_api_url"
        />
      </div>
    </div>
  </a>
</template>

<script>
import Favourite from "./Favourite.vue"
import CURRENT_QUERY from "@/queries/Current.query.gql"
import ShortlistModal from "@/pages/Shortlist/ShortlistModal.vue"
import Medium from "./Medium.vue"

export default {
  name: "Listing.Item",
  data() {
    return {
      inCart: false,
      showShortlistModal: false,
    }
  },
  apollo: {
    currentUser: {
      query: CURRENT_QUERY,
    },
  },
  props: {
    soldRibbon: {
      type: Boolean,
      required: false,
      default: true,
    },
    listing: {
      type: Object,
      required: true,
      default: {},
    },
    priceFilterApplied: {
      type: Boolean,
      default: false,
    },
    sizeFilterApplied: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.inCart = this.listing.in_cart
  },
  methods: {
    addToCart() {
      this.inCart = true
    },
  },
  computed: {
    shortlistVisible() {
      return this.listing.competition_category
    },
    isAdmin() {
      return this.currentUser?.admin
    },
    availabilityMode() {
      if (!this.listing.reprint && (this.listing.dispatch_from_date || this.listing.artist.holiday_mode)) {
        return "modal"
      }
    },
    listingUrl() {
      if (this.listing.reprint && !this.listing.sold && !this.priceFilterApplied && !this.sizeFilterApplied) {
        return this.listing.url
      }
      return this.listing.main_listing_result_url
    },
    priceLabel() {
      const price = (this.listing.price_with_discount || this.listing.price).format

      if (this.listing.reprint) return `from ${price}`

      return price
    },
  },
  components: {
    Medium,
    Favourite,
    ShortlistModal,
  },
}
</script>
